import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import Script from "next/script";
import { useMachine } from "@xstate/react";
import { useMemberProducts } from "@nju/data-access-portal-product";
import { ErrorMessage, Input } from "@nju/form";
import { portalRoutes } from "@nju/routes";
import {
  A,
  BlockText,
  Button,
  GeneralErrorNotification,
  Panel,
  Separator,
  Text,
} from "@nju/ui";
import { tvRegistrationMachine } from "./tvRegistration.machine";
import { TvRegistrationSuccess } from "./TvRegistrationSuccess";
import { ITvRegistrationMode } from "./types";

interface ITvRegistrationFormProps {
  option: ITvRegistrationMode;
}

export function TvRegistrationForm({ option }: ITvRegistrationFormProps) {
  const { mobileProducts } = useMemberProducts();
  const { television } = mobileProducts;
  const pepperInput = process.env.NEXT_PUBLIC_CONFIDENTIAL_PEPPER_INPUT;
  const router = useRouter();
  const route = portalRoutes.mojeKonto.index.input(router.query).unwrap();
  const userId = route?.mode === "mobile" ? route.userId : undefined;

  const [current, send] = useMachine(tvRegistrationMachine, {
    context: {
      tvId: television?.id,
      pepperInput,
      userId,
    },
  });

  const { context, matches } = current;
  const { code, codeError } = context;

  const header =
    option === "ADD_CODE" ? "stwórz swój kod poufny" : "zmień kod poufny";
  const content =
    option === "ADD_CODE"
      ? "Aby korzystać z usługi, załóż konto. Zacznij od stworzenia swojego kodu poufnego.\n Twój kod musi składać się z 4 cyfr i nie mogą to być 4 zera."
      : "Stwórz nowy kod poufny. Pamiętaj, że musi się on składać z 4 cyfr i nie mogą to być 4 zera.";

  const onInputChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      ({ target }) => {
        const { value } = target;

        send({
          type: "SET_CODE",
          value,
        });
      },
      [send],
    );

  const handleSubmit: React.FormEventHandler<HTMLFormElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        send({ type: "SUBMIT" });
      },
      [send],
    );

  if (matches("setCodeSuccess")) {
    return <TvRegistrationSuccess option={option} userId={userId} />;
  }

  if (matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return window.location.reload();
        }}
      />
    );
  }

  return (
    <Panel testId="tv-registration-form">
      <Text
        size={{
          "@onlyMobile": "xl",
          "@fromTablet": "keyS",
        }}
        weight="bold"
      >
        {header}
      </Text>
      <Separator size="xs" tone="invisible" />
      <BlockText
        className="whitespace-pre-line"
        size={{
          "@onlyMobile": "m",
          "@fromTablet": "m",
        }}
      >
        {content}
      </BlockText>
      <Separator size="s" tone="invisible" />
      <form onSubmit={handleSubmit}>
        <Script src="/scripts/crypto-api.min.js" />
        <div>
          <Input
            label="kod poufny"
            maxLength={4}
            name="confidential-code"
            onChange={onInputChange}
            state={codeError ? "error" : undefined}
            value={code || ""}
          />
        </div>
        {codeError && (
          <>
            <Separator size="xxs" tone="invisible" />
            <ErrorMessage message={codeError} />{" "}
          </>
        )}
        <Separator size="s" tone="invisible" />
        <div className="flex justify-between only-mobile:gap-3 only-mobile:flex-col-reverse flex-row">
          <Link
            href={portalRoutes.mojeKonto.index.get({ mode: "mobile" })}
            passHref={true}
          >
            <A
              size={{
                "@onlyMobile": "s",
              }}
              variant="secondary"
            >
              anuluj
            </A>
          </Link>
          <Button
            isLoading={!matches("ready")}
            size={{ "@onlyMobile": "s" }}
            type="submit"
          >
            dalej
          </Button>
        </div>
      </form>
    </Panel>
  );
}
